import React, { useCallback, useEffect, useState } from 'react';

import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';

import { Container, Frame } from './styled';

const FRAME_OFFSET = 70;

interface IFrameResult {
    url: string,
    onResponse: (args: Record<string, string>) => void,
    payParams?: unknown,
}

interface MessageFrameEvent extends MessageEvent<string> {
    originalEvent?: {
        data?: unknown,
    },
}

const isHTMLIFrameElement = (data: unknown): data is HTMLIFrameElement => {
    if (!data || typeof data !== 'object') {
        return false;
    }

    return 'contentWindow' in data;
};

const FrameResult = ({ url, onResponse, payParams }: IFrameResult) => {
    const [iframeHeight, setIframeHeight] = useState(0);

    const parseSetHeight = useCallback((message: string) => {
        const height = parseInt(message) + FRAME_OFFSET;
        setIframeHeight(height);
    }, []);

    const parsePaymentResponse = useCallback((message: string) => {
        const result = message.split('&').reduce(
            (acc: Record<string, string>, param) => {
                const [key, value] = param.split('=');
                acc[key] = value;

                return acc;
            },
            {},
        );

        onResponse(result);
    }, [onResponse]);

    const parseLoad = useCallback(() => {
        const frame = window.document.getElementById('paymentFrame');

        if (frame && isHTMLIFrameElement(frame)) {
            const contentWindow = frame.contentWindow;
            contentWindow?.postMessage({ payDate: payParams }, '*');
        }
    }, [payParams]);

    useEffect(() => {
        const messageListener = (event: MessageFrameEvent) => {
            const data = event.data || event.originalEvent?.data;
            if (data && typeof data === 'string') {
                const [command, message] = data.split(':', 2);
                const actionsMap: Record<string, () => void> = {
                    setHeight: () => parseSetHeight(message),
                    paymentResponse: () => parsePaymentResponse(message),
                    load: () => parseLoad(),
                };

                if (command in actionsMap) {
                    actionsMap[command]();
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    }, [onResponse, parseLoad, parsePaymentResponse, parseSetHeight, payParams]);

    const isLoading = !url;
    return (
        <Container
            loading={isLoading}
            data-test='paymentFrame'
            className={'ym-hide-content'}
        >
            {isLoading
                ? <Throbber position={'absolute'}/>
                : <Frame id='paymentFrame' src={url} height={iframeHeight}/>
            }
        </Container>
    );
};

export default FrameResult;
